import { useId, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { tr } from '@/lib/i18n-core';
import { cn, display } from '@/lib/utils';
import { useReducedMotion } from '@/lib/motion';
import {
  AnimatedNumber,
  ChartTooltip,
  EmptyChart,
  formatCount,
  LegendDot,
  type TooltipItem,
} from './dashboard-ui';

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 11 };
const GRID_STROKE = 'hsl(var(--border))';

export interface Series {
  key: string;
  label: string;
  color: string;
}

function gradientId(base: string, key: string): string {
  return `${base.replace(/[^a-zA-Z0-9_-]/g, '')}-${key}`;
}

export function dayLabel(date: string, style: 'short' | 'long' = 'short'): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(display.locale, {
    ...(style === 'long' ? { weekday: 'long' } : { weekday: 'short' }),
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function dayTick(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(display.locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function hourLabel(hour: number): string {
  return new Intl.DateTimeFormat(display.locale, { hour: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, 0, 1, hour)),
  );
}

export function Sparkline({ data, color }: { data: number[]; color: string }) {
  const reduced = useReducedMotion();
  const id = gradientId(useId(), 'spark');
  const points = data.map((value, index) => ({ index, value }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${id})`}
          isAnimationActive={!reduced}
          animationDuration={1100}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function SeriesToggles({
  series,
  hidden,
  onToggle,
  totals,
}: {
  series: Series[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  totals?: Record<string, ReactNode>;
}) {
  return (
    <div className="mb-3 flex flex-wrap gap-1.5">
      {series.map((item) => {
        const off = hidden.has(item.key);
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={!off}
            onClick={() => onToggle(item.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-all focus-ring',
              off
                ? 'text-muted-foreground opacity-60 hover:opacity-100'
                : 'bg-card font-medium shadow-sm',
            )}
          >
            <span
              className="h-2 w-2 rounded-full transition-transform"
              style={{ background: item.color, transform: off ? 'scale(0.6)' : 'scale(1)' }}
              aria-hidden
            />
            {item.label}
            {totals?.[item.key] !== undefined && (
              <span className="tabular-nums text-muted-foreground">{totals[item.key]}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TrendChart<Row extends { date: string }>({
  data,
  series,
  height = 240,
  valueFormatter,
  axisFormatter,
  totals,
}: {
  data: Row[];
  series: Series[];
  height?: number;
  valueFormatter?: (value: number) => string;
  axisFormatter?: (value: number) => string;
  totals?: Record<string, ReactNode>;
}) {
  const reduced = useReducedMotion();
  const base = useId();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else if (next.size < series.length - 1) next.add(key);
      return next;
    });

  return (
    <>
      {series.length > 1 && (
        <SeriesToggles series={series} hidden={hidden} onToggle={toggle} totals={totals} />
      )}
      <div className="min-h-0 flex-1" style={{ minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
            <defs>
              {series.map((item) => (
                <linearGradient
                  key={item.key}
                  id={gradientId(base, item.key)}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={item.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={item.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={dayTick}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={axisFormatter ? 64 : 44}
              tickFormatter={axisFormatter}
            />
            <Tooltip
              cursor={{
                stroke: 'hsl(var(--muted-foreground))',
                strokeDasharray: '4 4',
                strokeOpacity: 0.5,
              }}
              content={
                <ChartTooltip
                  labelFormatter={(label) => dayLabel(String(label), 'long')}
                  valueFormatter={valueFormatter ? (value) => valueFormatter(value) : undefined}
                />
              }
            />
            {series.map((item, index) =>
              hidden.has(item.key) ? null : (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={2.25}
                  fill={`url(#${gradientId(base, item.key)})`}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--card))' }}
                  dot={false}
                  isAnimationActive={!reduced}
                  animationBegin={index * 120}
                  animationDuration={1100}
                />
              ),
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

export function HourlyChart({
  data,
  currentHour,
  height = 220,
}: {
  data: { hour: number; today: number; typical: number }[];
  currentHour: number;
  height?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <div className="min-h-0 flex-1" style={{ minHeight: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
          <XAxis
            dataKey="hour"
            tickFormatter={hourLabel}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            minTickGap={12}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={44}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
            content={
              <ChartTooltip
                labelFormatter={(label) =>
                  `${hourLabel(Number(label))} – ${hourLabel((Number(label) + 1) % 24)}`
                }
                valueFormatter={(value, item: TooltipItem) =>
                  item.dataKey === 'typical' ? value.toFixed(1) : formatCount(value)
                }
              />
            }
          />
          <Bar
            dataKey="today"
            name={tr('admin', 'dashboard_today')}
            radius={[5, 5, 0, 0]}
            maxBarSize={28}
            isAnimationActive={!reduced}
            animationDuration={900}
          >
            {data.map((row) => (
              <Cell
                key={row.hour}
                fill="hsl(var(--primary))"
                fillOpacity={row.hour === currentHour ? 1 : row.hour > currentHour ? 0.25 : 0.7}
              />
            ))}
          </Bar>
          <Line
            type="monotone"
            dataKey="typical"
            name={tr('admin', 'dashboard_typical_day')}
            stroke="hsl(var(--chart-4))"
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            isAnimationActive={!reduced}
            animationBegin={300}
            animationDuration={1100}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PairedBarChart<Row extends { date: string }>({
  data,
  series,
  valueFormatter,
  axisFormatter,
  height = 240,
}: {
  data: Row[];
  series: [Series, Series];
  valueFormatter: (value: number) => string;
  axisFormatter: (value: number) => string;
  height?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <div className="min-h-0 flex-1" style={{ minHeight: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -4 }} barGap={2}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={dayTick}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={axisFormatter}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
            content={
              <ChartTooltip
                labelFormatter={(label) => dayLabel(String(label), 'long')}
                valueFormatter={(value) => valueFormatter(value)}
              />
            }
          />
          {series.map((item, index) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              fill={item.color}
              fillOpacity={index === 0 ? 0.35 : 1}
              radius={[4, 4, 0, 0]}
              maxBarSize={18}
              isAnimationActive={!reduced}
              animationBegin={index * 150}
              animationDuration={900}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface Slice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export function DonutChart({
  slices,
  centerLabel,
  centerValue,
  format = formatCount,
  empty,
  size = 176,
}: {
  slices: Slice[];
  centerLabel: string;
  centerValue?: number;
  format?: (value: number) => string;
  empty: ReactNode;
  size?: number;
}) {
  const reduced = useReducedMotion();
  const visible = slices.filter((slice) => slice.value > 0);
  const total = visible.reduce((sum, slice) => sum + slice.value, 0);

  if (total === 0) return <EmptyChart className="min-h-[11rem]">{empty}</EmptyChart>;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible}
              dataKey="value"
              nameKey="label"
              innerRadius="68%"
              outerRadius="100%"
              paddingAngle={visible.length > 1 ? 2 : 0}
              cornerRadius={4}
              stroke="none"
              startAngle={90}
              endAngle={-270}
              isAnimationActive={!reduced}
              animationDuration={1000}
            >
              {visible.map((slice) => (
                <Cell key={slice.key} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  valueFormatter={(value) =>
                    `${format(value)} · ${Math.round((value / total) * 100)}%`
                  }
                />
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>
            <p className="text-xl font-semibold leading-none">
              <AnimatedNumber value={centerValue ?? total} format={format} />
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">{centerLabel}</p>
          </div>
        </div>
      </div>
      <ul className="grid w-full grid-cols-2 gap-x-5 gap-y-2">
        {slices.map((slice) => (
          <li key={slice.key} className={cn(slice.value === 0 && 'opacity-50')}>
            <LegendDot color={slice.color} label={slice.label} value={format(slice.value)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RadialGauge({
  pct,
  color,
  label,
  detail,
}: {
  pct: number;
  color: string;
  label: string;
  detail: ReactNode;
}) {
  const reduced = useReducedMotion();
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative mx-auto h-44 w-full max-w-[15rem]">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          data={[{ value: clamped }]}
          startAngle={210}
          endAngle={-30}
          innerRadius="78%"
          outerRadius="100%"
          barSize={14}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
          <RadialBar
            dataKey="value"
            cornerRadius={10}
            fill={color}
            background={{ fill: 'hsl(var(--muted))' }}
            isAnimationActive={!reduced}
            animationDuration={1200}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 grid place-items-center pt-2 text-center">
        <div>
          <p className="text-3xl font-semibold leading-none tracking-tight">
            <AnimatedNumber value={clamped} format={(value) => `${Math.round(value)}%`} />
          </p>
          <p className="mt-1 text-xs font-medium">{label}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{detail}</p>
        </div>
      </div>
    </div>
  );
}
